import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

export const MemberTransactionsModal = ({ member, project, transactions, onClose, formatCurrency }) => {
  const [expandedTxs, setExpandedTxs] = useState(new Set());

  const toggleExpand = (id) => {
    const next = new Set(expandedTxs);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedTxs(next);
  };

  if (!member) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-white">
          <div>
            <h2 className="text-lg font-bold text-slate-800 tracking-tight flex items-center gap-2">
              {member.name}'s Transactions
            </h2>
            <p className="text-[13px] text-slate-500 font-medium">
              Total {transactions?.length || 0} transaction(s) found
            </p>
          </div>
          <div className="flex items-center gap-3">

            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto custom-scrollbar flex-1 bg-slate-50">
          {!transactions?.length ? (
            <div className="text-center py-12 text-slate-500 font-medium text-[14px]">
              No transactions found for this member.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {transactions.map((tx) => {
                const isExpanded = expandedTxs.has(tx._id);
                return (
                  <div key={tx._id} className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                    {/* Compact Row */}
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => toggleExpand(tx._id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col">
                          <span className="text-[12px] text-slate-500 font-medium">
                            {tx.transactionDate ? format(new Date(tx.transactionDate), 'dd MMM yyyy') : '—'}
                          </span>
                          <span className="text-[14px] font-semibold text-slate-800">
                            {tx.categoryLabel || tx.expenseCategory || 'General'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="flex flex-col items-end">
                          <span className="text-[12px] text-slate-500 font-medium">Amount</span>
                          <span className="text-[14px] font-bold text-[#2b448f]">
                            ₹{formatCurrency(tx.approvedAmount ?? tx.amount ?? 0)}
                          </span>
                        </div>
                        <div className="w-8 flex items-center justify-center text-slate-400">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 py-4 bg-slate-50 border-t border-slate-100 text-[13px] text-slate-600">
                        <div className="grid grid-cols-2 gap-4">
                          {tx.requestedBy && (
                            <div>
                              <p className="font-semibold text-slate-700 mb-1">Requested By</p>
                              <p>{tx.requestedBy?.name || tx.requestedBy}</p>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-700 mb-1">Reason</p>
                            <p>{tx.expenseReason || '—'}</p>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-700 mb-1">Requested Amount</p>
                            <p>₹{formatCurrency(tx.amount || 0)}</p>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-700 mb-1">Disbursed Amount</p>
                            <p>₹{formatCurrency(tx.disbursedAmount || 0)}</p>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-700 mb-1">Status</p>
                            <span className="uppercase font-bold text-[11px] px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                              {tx.requestStatus}
                            </span>
                          </div>

                          <div>
                            <p className="font-semibold text-slate-700 mb-1">Related to Project?</p>
                            <p>{tx.relatedToProject ? 'Yes' : 'No'}</p>
                          </div>

                          {/* Role Type, EP Type & To Whom */}
                          {tx.roleType && tx.roleType !== 'not_applicable' && (
                            <div>
                              <p className="font-semibold text-slate-700 mb-1">Role Type</p>
                              <p className="capitalize">{tx.roleType}</p>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-700 mb-1">Payment Type</p>
                            <p className="capitalize">
                              {(!tx.epType || tx.epType === 'not_applicable') 
                                ? 'Expense' 
                                : String(tx.epType).replace(/_/g, ' ')}
                            </p>
                          </div>
                          {tx.expenseToWhom && (
                            <div>
                              <p className="font-semibold text-slate-700 mb-1">Paid To / To Whom</p>
                              <p>{tx.expenseToWhom}</p>
                            </div>
                          )}

                          {/* EP Breakdowns */}
                          {(tx.standardAmount > 0 || tx.epAmount > 0 || tx.nonStandardAmount > 0 || tx.othersAmount > 0) && (
                            <div className="col-span-2 mt-2 pt-3 border-t border-slate-100">
                              <p className="font-bold text-slate-800 mb-2">EP Type Breakdown</p>
                              <div className="grid grid-cols-4 gap-4">
                                {tx.standardAmount > 0 && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1 text-[11px] uppercase tracking-wider">Standard</p>
                                    <p>₹{formatCurrency(tx.standardAmount)}</p>
                                  </div>
                                )}
                                {tx.epAmount > 0 && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1 text-[11px] uppercase tracking-wider">EP</p>
                                    <p>₹{formatCurrency(tx.epAmount)}</p>
                                  </div>
                                )}
                                {tx.nonStandardAmount > 0 && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1 text-[11px] uppercase tracking-wider">Non-Std</p>
                                    <p>₹{formatCurrency(tx.nonStandardAmount)}</p>
                                  </div>
                                )}
                                {tx.othersAmount > 0 && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1 text-[11px] uppercase tracking-wider">Others</p>
                                    <p>₹{formatCurrency(tx.othersAmount)}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Notes Section */}
                          {(tx.expenseNotes || tx.approvalNote || tx.internalNotes) && (
                            <div className="col-span-2 mt-2 pt-3 border-t border-slate-100 flex flex-col gap-3">
                              <p className="font-bold text-slate-800 mb-1">Notes</p>
                              {tx.expenseNotes && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1 text-[12px]">Raise Notes</p>
                                  <p className="text-slate-600 bg-white p-2 rounded border border-slate-100">{tx.expenseNotes}</p>
                                </div>
                              )}
                              {tx.approvalNote && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1 text-[12px]">Approval Notes</p>
                                  <p className="text-slate-600 bg-white p-2 rounded border border-slate-100">{tx.approvalNote}</p>
                                </div>
                              )}
                              {tx.internalNotes && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1 text-[12px]">Disburse / Internal Notes</p>
                                  <p className="text-slate-600 bg-white p-2 rounded border border-slate-100">{tx.internalNotes}</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Payment Details Section */}
                          {(tx.paymentMethod || tx.refId || tx.bankName || tx.upiPhone || tx.chequeNumber) && (
                            <div className="col-span-2 mt-2 pt-2 border-t border-slate-200">
                              <p className="font-bold text-slate-800 mb-2">Payment Information</p>
                              <div className="grid grid-cols-2 gap-4">
                                {tx.paymentMethod && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">Method</p>
                                    <p className="capitalize">{String(tx.paymentMethod).replace('_', ' ')}</p>
                                  </div>
                                )}
                                {tx.refId && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">Reference ID</p>
                                    <p>{tx.refId}</p>
                                  </div>
                                )}
                                {tx.bankName && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">Bank Name</p>
                                    <p>{tx.bankName} {tx.branch ? `(${tx.branch})` : ''}</p>
                                  </div>
                                )}
                                {tx.upiPhone && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">UPI / Phone</p>
                                    <p>{tx.upiPhone}</p>
                                  </div>
                                )}
                                {tx.receiverName && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">Receiver Name</p>
                                    <p>{tx.receiverName}</p>
                                  </div>
                                )}
                                {tx.chequeNumber && (
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">Cheque Number</p>
                                    <p>{tx.chequeNumber} {tx.chequeDate ? `(${format(new Date(tx.chequeDate), 'dd MMM yyyy')})` : ''}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
